import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Save, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CallerNotesSection = () => {
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const saveTimeout = useRef(null);

  useEffect(() => {
    const fetchNotes = async () => {
      try {
        const { data } = await axios.get(`${API}/notes`, { withCredentials: true });
        setNotes(data.notes || '');
      } catch (err) {
        console.warn('Could not load caller notes:', err);
      }
    };
    fetchNotes();
  }, []);

  const saveNotes = useCallback(async (text) => {
    setSaving(true);
    try {
      await axios.post(`${API}/notes`, { notes: text }, { withCredentials: true });
      setLastSaved(new Date());
    } catch {
      toast.error('Failed to save notes');
    } finally {
      setSaving(false);
    }
  }, []);

  const handleChange = (e) => {
    const text = e.target.value;
    setNotes(text);
    // Auto-save after 1.5 seconds of inactivity
    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(() => saveNotes(text), 1500);
  };

  const handleManualSave = () => {
    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    saveNotes(notes);
  };

  return (
    <div className="content-card content-card-large" data-testid="caller-notes-section">
      <div className="card-header">
        <h2>
          <FileText size={20} className="inline" /> Caller Notes
        </h2>
        <button 
          className="section-action-btn" 
          onClick={handleManualSave}
          disabled={saving}
          title="Save notes"
          data-testid="save-notes-btn"
        >
          <Save size={16} />
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
      <div className="card-content">
        <textarea
          className="caller-notes-textarea"
          value={notes}
          onChange={handleChange}
          placeholder="Type your caller notes, reminders, show prep here...&#10;&#10;Notes auto-save after you stop typing."
          data-testid="caller-notes-textarea"
        />
        {lastSaved && (
          <div className="notes-saved-indicator" data-testid="notes-saved-time">
            Last saved: {lastSaved.toLocaleTimeString()}
          </div>
        )}
      </div>
    </div>
  );
};

export default CallerNotesSection;
